<template>
  <div class="tool-card ai-visibility-card pa-5" :class="{ 'is-start-here': startHere }">
    <div class="top-part">
      <span class="icon-part ai-visibility-card__icon">
        <span class="material-icons-outlined">visibility</span>
      </span>
      <span class="name">AI Visibility</span>
      <span v-if="startHere" class="start-here-pill">START HERE</span>
      <span v-else class="ai-visibility-card__tag">NEW</span>
    </div>

    <div class="flex-grow-1 pages-wrapper mt-3 ai-visibility-card__trackers">
      <div v-if="loading" class="ai-visibility-card__empty">
        Loading trackers...
      </div>

      <div v-else-if="loadError" class="ai-visibility-card__error">
        {{ loadError }}
      </div>

      <template v-else-if="trackers.length">
        <button
          v-for="tracker in trackers"
          :key="tracker.id"
          type="button"
          class="page-item ai-visibility-card__tracker-row"
          @click="openTracker(tracker)"
        >
          <div class="flex-grow-1">
            <div class="name">{{ trackerTitle(tracker) }}</div>
            <div class="ai-visibility-card__tracker-meta">
              <span class="time">{{ toLocalDateTimeString(tracker.lastRunAt) || '' }}</span>
              <ai-visibility-refresh-chip
                :refresh-frequency="tracker.refreshFrequency"
                :status="tracker.status"
              />
            </div>
          </div>
          <div class="action">
            <i class="material-icons-outlined">arrow_circle_right</i>
          </div>
        </button>
      </template>

      <div v-else class="no-project-block">
        <div>
          <img src="/static/empty-folder.png" />
          <div>
            You haven't created any <br />
            reports yet
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5 d-flex align-center justify-space-between">
      <v-btn
        rounded
        small
        color="profileAnchorColor"
        class="button-with-icon whiteColor--text text-none"
        @click="openSetupDialog"
      >
        <span>Create new</span>
      </v-btn>
      <span class="how-many">{{ trackers.length }} {{ trackers.length === 1 ? 'report' : 'reports' }}</span>
    </div>

    <ai-visibility-setup-dialog
      ref="setupDialogRef"
      :site-id="siteId"
      :site="site"
      :latest-tracker="latestTracker"
      @created="handleTrackerCreated"
    />

  </div>
</template>

<script>
import AiVisibilitySetupDialog from '../setup/AiVisibilitySetupDialog.vue'
import AiVisibilityRefreshChip from './AiVisibilityRefreshChip.vue'
import { getAiVisibilityProgress, getAiVisibilityTrackers } from '../api'
import { EventBus, toLocalDateTimeString } from '@/utils'
import { AI_VISIBILITY_RUN_PROGRESS_STARTED } from '../progress/events'
import { planGateMixin } from '@/components/Auth/plan-gate'
import { startHereMixin } from '@/components/SitesSingleView/Tools/start-here'

const PROGRESS_POLL_INTERVAL_MS = 5000

export default {
  name: 'AiVisibilityToolCard',
  // the signup redesign added requirePlan() to "Create new" and the startHere highlight without
  // their mixins, so the button threw and the START HERE pill never showed (tester 2026-10-22)
  mixins: [startHereMixin, planGateMixin],
  components: {
    AiVisibilityRefreshChip,
    AiVisibilitySetupDialog,
  },
  data () {
    return {
      loading: false,
      loadError: '',
      trackers: [],
      progressTracker: null,
      runProgress: null,
      progressTimer: null,
      progressRequestInFlight: false,
      progressStartedFromCreation: false,
    }
  },
  computed: {
    siteId () {
      return parseInt(this.$route.params.siteId)
    },
    site () {
      return this.$store.getters.siteById(this.siteId) || null
    },
    latestTracker () {
      if (this.trackers.length > 0) {
        return this.trackers[0]
      }
      return null
    }
  },
  methods: {
    toLocalDateTimeString (d) {
      return toLocalDateTimeString(d)
    },
    openSetupDialog () {
      if (!this.requirePlan(null, 'aivis')) return
      this.$refs.setupDialogRef.showDialog()
    },
    trackerTitle (tracker) {
      return tracker?.title || tracker?.name || tracker?.domain || 'AI Visibility tracker'
    },
    openTracker (tracker) {
      if (!tracker || tracker.id == null || !this.siteId) {
        return
      }

      this.$router.push({
        name: 'AiVisibilityOverview',
        params: {
          siteId: String(this.siteId),
          trackerId: String(tracker.id),
        },
      })
    },
    handleTrackerCreated (tracker) {
      if (tracker) {
        this.loadTrackers()
        EventBus.$emit(AI_VISIBILITY_RUN_PROGRESS_STARTED, {
          siteId: this.siteId,
          tracker,
          fromCreation: true,
        })
        this.startProgressPolling(tracker, { fromCreation: true })
      }
    },
    startProgressPolling (tracker, options = {}) {
      if (!tracker || tracker.id == null) {
        return
      }

      this.clearProgressTimer()
      this.progressTracker = tracker
      this.progressStartedFromCreation = Boolean(options.fromCreation)
      this.runProgress = options.waitForInitialProgress
        ? null
        : {
            percentage: 0,
            isComplete: false,
            hasReadyRun: false,
          }
      this.pollProgress()
      this.progressTimer = setInterval(
        this.pollProgress,
        PROGRESS_POLL_INTERVAL_MS,
      )
    },
    pollProgress () {
      if (
        !this.progressTracker ||
        this.progressTracker.id == null ||
        this.progressRequestInFlight
      ) {
        return Promise.resolve()
      }

      this.progressRequestInFlight = true
      return getAiVisibilityProgress(this.siteId, this.progressTracker.id)
        .then((response) => {
          this.runProgress = {
            percentage: 0,
            isComplete: false,
            hasReadyRun: false,
            ...(response.data || {}),
          }
          if (
            !this.progressStartedFromCreation &&
            !this.isFirstRunProgress(this.runProgress)
          ) {
            this.stopProgressPolling()
            return
          }
          if (this.runProgress.isComplete) {
            this.clearProgressTimer()
            this.loadTrackers()
          }
        })
        .catch(() => {
          this.stopProgressPolling()
        })
        .finally(() => {
          this.progressRequestInFlight = false
        })
    },
    clearProgressTimer () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    stopProgressPolling () {
      this.clearProgressTimer()
      this.progressTracker = null
      this.runProgress = null
      this.progressRequestInFlight = false
      this.progressStartedFromCreation = false
    },
    isFirstRunProgress (progress) {
      return Boolean(progress && progress.hasReadyRun === false)
    },
    syncProgressPollingFromTrackers () {
      const runningTracker = this.trackers.find((tracker) => tracker.isRunning)

      if (!runningTracker) {
        if (!this.progressStartedFromCreation) {
          this.stopProgressPolling()
        }
        return
      }

      if (
        this.progressTracker &&
        String(this.progressTracker.id) === String(runningTracker.id)
      ) {
        this.progressTracker = runningTracker
        return
      }

      if (this.progressStartedFromCreation && this.progressTracker) {
        return
      }

      this.startProgressPolling(runningTracker, {
        waitForInitialProgress: true,
      })
    },
    loadTrackers () {
      if (!this.siteId) {
        return
      }

      this.loading = true
      this.loadError = ''
      return getAiVisibilityTrackers(this.siteId)
        .then((response) => {
          this.trackers = response.data.trackers || []
          this.syncProgressPollingFromTrackers()
        })
        .catch((error) => {
          const data = (error && error.response && error.response.data) ? error.response.data : {}
          this.loadError = data.msg || data.error || 'Could not load AI Visibility trackers.'
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
  beforeMount () {
    this.loadTrackers()
  },
  beforeDestroy () {
    this.stopProgressPolling()
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-card {
  border-color: var(--v-profileAnchorColor-base) !important;
}

.ai-visibility-card__icon {
  border: 1px solid var(--v-profileAnchorColor-base);

  span {
    color: var(--v-profileAnchorColor-base) !important;
  }
}

.ai-visibility-card__tag {
  border: 1px solid var(--v-profileAnchorColor-base);
  border-radius: 6px;
  background: var(--v-lightYellowColor-base);
  color: var(--v-profileAnchorColor-base);
  font-size: 14px;
  line-height: 1.4;
  padding: 8px 10px;
}

.ai-visibility-card__trackers {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ai-visibility-card__tracker-row {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
  width: 100%;
}

.ai-visibility-card__tracker-row:focus-visible {
  outline: 2px solid var(--v-profileAnchorColor-base);
  outline-offset: 2px;
}

.ai-visibility-card__tracker-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.ai-visibility-card__empty {
  color: var(--v-mainColor-base);
  font-size: 14px;
  text-align: center;
}

.ai-visibility-card__error {
  color: var(--v-red11Color-base);
  font-size: 14px;
  text-align: center;
}
</style>
